import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { FilterCreatorsDto } from '@/app/common/general-types/filters.dto';
import { genderOptions } from '@/app/common/app-config/filter-models/gender';
import { interestsList } from '@/app/common/app-config/filter-models/interests';
import {
  FOLLOWER_RANGE_DEFAULT,
  FOLLOWER_RANGE_MAX_INDEX,
  FOLLOWER_RANGE_STEP_LABELS,
  FollowerRangeValue,
  followerStepIndexToValue,
  followerValueToStepIndex,
  formatFollowerRangeLabel,
  formatFollowerStepLabel,
  isDefaultFollowerRange,
} from '@/app/common/app-config/filter-models/follower-range';
import {
  emptyPlatformPerformanceDraft,
  ENGAGEMENT_RATE_OPTIONS,
  formatPerformanceRangeLabel,
  formatPerformanceStepLabel,
  isDefaultPerformanceRange,
  PERFORMANCE_RANGE_MAX_INDEX,
  PERFORMANCE_RANGE_TICK_LABELS,
  PerformancePlatform,
  PerformanceRangeValue,
  PlatformPerformanceDraft,
  performanceStepIndexToValue,
  performanceValueToStepIndex,
} from '@/app/common/app-config/filter-models/performance-range';
import { MagicSearchService } from '../../../creators/service/magic-search/magic-search.service';
import {
  emptyFindCreatorsFilterDraft,
  FIND_CREATORS_COUNTRY_OPTIONS,
  FIND_CREATORS_RATING_OPTIONS,
  FindCreatorsFilterDraft,
  findCreatorsDraftToDto,
  findCreatorsDtoToDraft,
  interestNames,
  mergeProfileFilters,
  stripProfileFilters,
} from './find-creators-filters.helpers';

type FollowerPlatformKey = 'tiktok' | 'instagram' | 'youtube' | 'snapchat';
type FollowerSlider = { minValue: number; maxValue: number };
type PerformanceMetricKey = 'averageViews' | 'totalViews' | 'averageLikes' | 'totalLikes';

export type FindCreatorsFiltersMode = 'trigger' | 'chips';

export interface FindCreatorsFilterChip {
  key: FindCreatorsFilterChipKey;
  label: string;
}

export type FindCreatorsFilterChipKey =
  | 'searchTerm'
  | 'country'
  | 'gender'
  | 'rating'
  | 'age'
  | 'isVerified'
  | 'hasReviews'
  | 'homeTownSearchTerm'
  | 'interests'
  | 'tikTokFollowers'
  | 'instagramFollowers'
  | 'youtubeSubscribers'
  | 'snapchatFollowers'
  | 'tikTokAverageEngagement'
  | 'tikTokAverageViews'
  | 'tikTokTotalViews'
  | 'tikTokAverageLikes'
  | 'tikTokTotalLikes'
  | 'instagramAverageEngagement'
  | 'instagramAverageViews'
  | 'instagramTotalViews'
  | 'instagramAverageLikes'
  | 'instagramTotalLikes'
  | 'youtubeAverageEngagement'
  | 'youtubeAverageViews'
  | 'youtubeTotalViews'
  | 'youtubeAverageLikes'
  | 'youtubeTotalLikes';

@Component({
  selector: 'cv4f-find-creators-filters',
  templateUrl: './find-creators-filters.html',
  styleUrl: './find-creators-filters.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatSliderModule, FormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: {
    class: 'items-center',
    '[class.inline-flex]': 'mode() === "trigger"',
    '[class.flex]': 'mode() === "chips"',
    '[class.min-w-0]': 'mode() === "chips"',
    '[class.flex-1]': 'mode() === "chips"',
  },
})
export class FindCreatorsFilters {
  @ViewChild('findCreatorsFiltersDialog')
  findCreatorsFiltersDialog?: ElementRef<HTMLDialogElement>;

  readonly mode = input<FindCreatorsFiltersMode>('trigger');

  readonly magicSearchService = inject(MagicSearchService);
  readonly draft = signal<FindCreatorsFilterDraft>(emptyFindCreatorsFilterDraft());

  readonly countryOptions = FIND_CREATORS_COUNTRY_OPTIONS;
  readonly ratingOptions = FIND_CREATORS_RATING_OPTIONS;
  readonly genderOptions = genderOptions;
  readonly nicheOptions = interestsList;
  readonly followerStepLabels = FOLLOWER_RANGE_STEP_LABELS;
  readonly performanceTickLabels = PERFORMANCE_RANGE_TICK_LABELS;
  readonly engagementRateOptions = ENGAGEMENT_RATE_OPTIONS;
  readonly platformTabs: { id: FollowerPlatformKey; label: string; iconSrc: string }[] = [
    { id: 'instagram', label: 'Instagram', iconSrc: 'assets/social-icon/instagram-logo.png' },
    { id: 'tiktok', label: 'TikTok', iconSrc: 'assets/social-icon/tik-tok-transparent.svg' },
    { id: 'youtube', label: 'YouTube', iconSrc: 'assets/social-icon/youtube_shorts.webp' },
    { id: 'snapchat', label: 'Snapchat', iconSrc: 'assets/social-icon/snapchat-logo.svg' },
  ];
  readonly selectedPlatform = signal<FollowerPlatformKey>('tiktok');
  readonly performancePlatform = computed((): PerformancePlatform | null => {
    const platform = this.selectedPlatform();
    return platform === 'snapchat' ? null : platform;
  });
  readonly followerRangeSliderDefaults = {
    disabled: false,
    max: FOLLOWER_RANGE_MAX_INDEX,
    min: 0,
    step: 1,
    showTicks: true,
    thumbLabel: false,
  };
  tiktokFollowerSlider: FollowerSlider = { minValue: 0, maxValue: FOLLOWER_RANGE_MAX_INDEX };
  instagramFollowerSlider: FollowerSlider = { minValue: 0, maxValue: FOLLOWER_RANGE_MAX_INDEX };
  youtubeFollowerSlider: FollowerSlider = { minValue: 0, maxValue: FOLLOWER_RANGE_MAX_INDEX };
  snapchatFollowerSlider: FollowerSlider = { minValue: 0, maxValue: FOLLOWER_RANGE_MAX_INDEX };
  readonly performanceRangeSliderDefaults = {
    disabled: false,
    max: PERFORMANCE_RANGE_MAX_INDEX,
    min: 0,
    step: 1,
    showTicks: false,
    thumbLabel: false,
  };
  tiktokAvgViewsSlider: FollowerSlider = { minValue: 0, maxValue: PERFORMANCE_RANGE_MAX_INDEX };
  tiktokTotalViewsSlider: FollowerSlider = { minValue: 0, maxValue: PERFORMANCE_RANGE_MAX_INDEX };
  tiktokAvgLikesSlider: FollowerSlider = { minValue: 0, maxValue: PERFORMANCE_RANGE_MAX_INDEX };
  tiktokTotalLikesSlider: FollowerSlider = { minValue: 0, maxValue: PERFORMANCE_RANGE_MAX_INDEX };
  instagramAvgViewsSlider: FollowerSlider = { minValue: 0, maxValue: PERFORMANCE_RANGE_MAX_INDEX };
  instagramTotalViewsSlider: FollowerSlider = { minValue: 0, maxValue: PERFORMANCE_RANGE_MAX_INDEX };
  instagramAvgLikesSlider: FollowerSlider = { minValue: 0, maxValue: PERFORMANCE_RANGE_MAX_INDEX };
  instagramTotalLikesSlider: FollowerSlider = { minValue: 0, maxValue: PERFORMANCE_RANGE_MAX_INDEX };
  youtubeAvgViewsSlider: FollowerSlider = { minValue: 0, maxValue: PERFORMANCE_RANGE_MAX_INDEX };
  youtubeTotalViewsSlider: FollowerSlider = { minValue: 0, maxValue: PERFORMANCE_RANGE_MAX_INDEX };
  youtubeAvgLikesSlider: FollowerSlider = { minValue: 0, maxValue: PERFORMANCE_RANGE_MAX_INDEX };
  youtubeTotalLikesSlider: FollowerSlider = { minValue: 0, maxValue: PERFORMANCE_RANGE_MAX_INDEX };
  readonly ageRangeSliderDefaults = {
    disabled: false,
    max: 65,
    min: 16,
    step: 1,
    showTicks: false,
    thumbLabel: false,
  };
  ageSlider: FollowerSlider = { minValue: 16, maxValue: 65 };

  readonly appliedFilters = computed(() => this.magicSearchService.filtersObjectSignal());

  readonly activeChipCount = computed(
    () => this.activeChips().filter((chip) => chip.key !== 'searchTerm').length,
  );

  readonly activeChips = computed((): FindCreatorsFilterChip[] => {
    const filters = this.appliedFilters();
    const chips: FindCreatorsFilterChip[] = [];

    if (filters.searchTerm) {
      chips.push({ key: 'searchTerm', label: `Keyword: ${filters.searchTerm}` });
    }

    if (filters.country?.length) {
      const code = filters.country[0];
      const name =
        FIND_CREATORS_COUNTRY_OPTIONS.find((c) => c.id === code)?.name ?? code;
      chips.push({ key: 'country', label: `Country: ${name}` });
    }

    if (filters.gender !== undefined && filters.gender !== null) {
      const genderId = Array.isArray(filters.gender)
        ? filters.gender[0]
        : Number(filters.gender);
      const name =
        genderOptions.find((g) => g.id === genderId)?.name ?? String(genderId);
      chips.push({ key: 'gender', label: `Gender: ${name}` });
    }

    if (filters.rating != null) {
      const ratingLabel =
        FIND_CREATORS_RATING_OPTIONS.find((o) => o.value === filters.rating)?.label
        ?? `${filters.rating}+`;
      chips.push({ key: 'rating', label: `Min rating: ${ratingLabel}` });
    }

    if (filters.ageFrom != null || filters.ageTo != null) {
      const from = filters.ageFrom ?? '…';
      const to = filters.ageTo ?? '…';
      chips.push({ key: 'age', label: `Age: ${from}–${to}` });
    }

    if (filters.isVerified === true) {
      chips.push({ key: 'isVerified', label: 'Verified only' });
    }

    if (filters.hasReviews === true) {
      chips.push({ key: 'hasReviews', label: 'Has reviews' });
    }

    if (filters.homeTownSearchTerm) {
      chips.push({
        key: 'homeTownSearchTerm',
        label: `Hometown: ${filters.homeTownSearchTerm}`,
      });
    }

    if (filters.interests?.length) {
      chips.push({
        key: 'interests',
        label: `Niches: ${interestNames(filters.interests)}`,
      });
    }

    this.pushFollowerChip(
      chips,
      'tikTokFollowers',
      'TikTok',
      filters.tikTokFollowersFrom,
      filters.tikTokFollowersTo,
    );
    this.pushFollowerChip(
      chips,
      'instagramFollowers',
      'Instagram',
      filters.instagramFollowersFrom,
      filters.instagramFollowersTo,
    );
    this.pushFollowerChip(
      chips,
      'youtubeSubscribers',
      'YouTube',
      filters.youtubeSubscribersFrom,
      filters.youtubeSubscribersTo,
    );
    this.pushFollowerChip(
      chips,
      'snapchatFollowers',
      'Snapchat',
      filters.snapchatFollowersFrom,
      filters.snapchatFollowersTo,
    );

    this.pushPerformanceChips(chips, filters, 'tikTok', 'TikTok');
    this.pushPerformanceChips(chips, filters, 'instagram', 'Instagram');
    this.pushPerformanceChips(chips, filters, 'youtube', 'YouTube');

    return chips;
  });

  openPanel(): void {
    this.draft.set(findCreatorsDtoToDraft(this.dtoFilters()));
    this.syncSlidersFromDraft();
  }

  closePanel(): void {
    this.findCreatorsFiltersDialog?.nativeElement?.close();
  }

  applyFilters(): void {
    this.publishFilters(
      mergeProfileFilters(this.dtoFilters(), findCreatorsDraftToDto(this.draft())),
    );
    this.closePanel();
  }

  clearFilters(): void {
    this.draft.set(emptyFindCreatorsFilterDraft());
    this.syncSlidersFromDraft();
    const next = stripProfileFilters(this.dtoFilters());
    delete next.searchTerm;
    this.magicSearchService.clearKeywordSearch();
    this.publishFilters(next);
    this.closePanel();
  }

  removeChip(key: FindCreatorsFilterChipKey): void {
    const current = { ...this.dtoFilters() };

    switch (key) {
      case 'searchTerm':
        delete current.searchTerm;
        this.magicSearchService.clearKeywordSearch();
        break;
      case 'country':
        delete current.country;
        break;
      case 'gender':
        delete current.gender;
        break;
      case 'rating':
        delete current.rating;
        break;
      case 'age':
        delete current.ageFrom;
        delete current.ageTo;
        break;
      case 'isVerified':
        delete current.isVerified;
        break;
      case 'hasReviews':
        delete current.hasReviews;
        break;
      case 'homeTownSearchTerm':
        delete current.homeTownSearchTerm;
        break;
      case 'interests':
        delete current.interests;
        break;
      case 'tikTokFollowers':
        delete current.tikTokFollowersFrom;
        delete current.tikTokFollowersTo;
        break;
      case 'instagramFollowers':
        delete current.instagramFollowersFrom;
        delete current.instagramFollowersTo;
        break;
      case 'youtubeSubscribers':
        delete current.youtubeSubscribersFrom;
        delete current.youtubeSubscribersTo;
        break;
      case 'snapchatFollowers':
        delete current.snapchatFollowersFrom;
        delete current.snapchatFollowersTo;
        break;
      case 'tikTokAverageEngagement':
        delete current.tikTokAverageEngagementFrom;
        break;
      case 'tikTokAverageViews':
        delete current.tikTokAverageViewsFrom;
        delete current.tikTokAverageViewsTo;
        break;
      case 'tikTokTotalViews':
        delete current.tikTokTotalViewsFrom;
        delete current.tikTokTotalViewsTo;
        break;
      case 'tikTokAverageLikes':
        delete current.tikTokAverageLikesFrom;
        delete current.tikTokAverageLikesTo;
        break;
      case 'tikTokTotalLikes':
        delete current.tikTokTotalLikesFrom;
        delete current.tikTokTotalLikesTo;
        break;
      case 'instagramAverageEngagement':
        delete current.instagramAverageEngagementFrom;
        break;
      case 'instagramAverageViews':
        delete current.instagramAverageViewsFrom;
        delete current.instagramAverageViewsTo;
        break;
      case 'instagramTotalViews':
        delete current.instagramTotalViewsFrom;
        delete current.instagramTotalViewsTo;
        break;
      case 'instagramAverageLikes':
        delete current.instagramAverageLikesFrom;
        delete current.instagramAverageLikesTo;
        break;
      case 'instagramTotalLikes':
        delete current.instagramTotalLikesFrom;
        delete current.instagramTotalLikesTo;
        break;
      case 'youtubeAverageEngagement':
        delete current.youtubeAverageEngagementFrom;
        break;
      case 'youtubeAverageViews':
        delete current.youtubeAverageViewsFrom;
        delete current.youtubeAverageViewsTo;
        break;
      case 'youtubeTotalViews':
        delete current.youtubeTotalViewsFrom;
        delete current.youtubeTotalViewsTo;
        break;
      case 'youtubeAverageLikes':
        delete current.youtubeAverageLikesFrom;
        delete current.youtubeAverageLikesTo;
        break;
      case 'youtubeTotalLikes':
        delete current.youtubeTotalLikesFrom;
        delete current.youtubeTotalLikesTo;
        break;
    }

    this.publishFilters(current);
  }

  selectCountry(countryId: string | null): void {
    this.draft.update((d) => ({ ...d, country: countryId }));
  }

  selectGender(genderId: number | null): void {
    this.draft.update((d) => ({ ...d, gender: genderId }));
  }

  selectRating(rating: number | null): void {
    this.draft.update((d) => ({ ...d, rating }));
  }

  toggleInterest(interestId: number): void {
    this.draft.update((d) => {
      const selected = d.interests.includes(interestId)
        ? d.interests.filter((id) => id !== interestId)
        : [...d.interests, interestId];
      return { ...d, interests: selected };
    });
  }

  isInterestSelected(interestId: number): boolean {
    return this.draft().interests.includes(interestId);
  }

  onAgeRangeChange(event: number, type: 'min' | 'max'): void {
    if (type === 'min') {
      this.ageSlider.minValue = event;
      if (this.ageSlider.minValue > this.ageSlider.maxValue) {
        this.ageSlider.maxValue = this.ageSlider.minValue;
      }
    } else {
      this.ageSlider.maxValue = event;
      if (this.ageSlider.maxValue < this.ageSlider.minValue) {
        this.ageSlider.minValue = this.ageSlider.maxValue;
      }
    }

    const isDefault =
      this.ageSlider.minValue === this.ageRangeSliderDefaults.min
      && this.ageSlider.maxValue === this.ageRangeSliderDefaults.max;
    this.draft.update((d) => ({
      ...d,
      ageFrom: isDefault ? null : this.ageSlider.minValue,
      ageTo: isDefault ? null : this.ageSlider.maxValue,
    }));
  }

  onHometownInput(value: string): void {
    this.draft.update((d) => ({ ...d, homeTownSearchTerm: value }));
  }

  formatFollowerStep(index: number): string {
    return formatFollowerStepLabel(followerStepIndexToValue(index));
  }

  formatPerformanceStep(index: number): string {
    return formatPerformanceStepLabel(performanceStepIndexToValue(index));
  }

  selectPlatform(platform: FollowerPlatformKey): void {
    this.selectedPlatform.set(platform);
  }

  currentPerformance(): PlatformPerformanceDraft {
    const platform = this.performancePlatform();
    if (!platform) {
      return emptyPlatformPerformanceDraft();
    }
    return this.performanceDraft(this.draft(), platform);
  }

  selectedFollowerSlider(): FollowerSlider {
    return this.getFollowerSlider(this.selectedPlatform());
  }

  performanceSlider(metric: PerformanceMetricKey): FollowerSlider {
    const platform = this.performancePlatform();
    if (!platform) {
      return this.tiktokAvgViewsSlider;
    }
    return this.getPerformanceSlider(platform, metric);
  }

  selectedFollowerLabel(): string {
    switch (this.selectedPlatform()) {
      case 'tiktok':
        return 'TikTok';
      case 'instagram':
        return 'Instagram';
      case 'youtube':
        return 'YouTube';
      case 'snapchat':
        return 'Snapchat';
    }
  }

  selectedFollowerIcon(): string {
    return this.platformTabs.find((tab) => tab.id === this.selectedPlatform())?.iconSrc
      ?? 'assets/social-icon/tik-tok-transparent.svg';
  }

  platformTabClass(platform: FollowerPlatformKey): string {
    return this.selectedPlatform() === platform
      ? 'bg-white dark:bg-zinc-800 text-primary shadow-sm border border-zinc-200 dark:border-white/10'
      : 'text-secondary hover:text-primary';
  }

  filterChipClass(selected: boolean): string {
    return selected
      ? 'btn-shout btn-shout-xs btn-shout-primary'
      : 'btn-shout btn-shout-xs btn-shout-secondary';
  }

  onEngagementSelect(raw: string): void {
    const platform = this.performancePlatform();
    if (!platform) {
      return;
    }
    const value = raw === '' ? null : Number(raw);
    this.selectEngagementRate(
      platform,
      value != null && Number.isFinite(value) ? value : null,
    );
  }

  selectEngagementRate(platform: PerformancePlatform, value: number | null): void {
    this.draft.update((d) => ({
      ...d,
      [this.performanceDraftKey(platform)]: {
        ...this.performanceDraft(d, platform),
        averageEngagement: value,
      },
    }));
  }

  onSelectedFollowerRangeChange(event: number, type: 'min' | 'max'): void {
    this.onFollowerRangeChange(this.selectedPlatform(), event, type);
  }

  onPerformanceRangeChange(
    platform: PerformancePlatform,
    metric: PerformanceMetricKey,
    event: number,
    type: 'min' | 'max',
  ): void {
    const slider = this.getPerformanceSlider(platform, metric);
    if (type === 'min') {
      slider.minValue = event;
      if (slider.minValue > slider.maxValue) {
        slider.maxValue = slider.minValue;
      }
    } else {
      slider.maxValue = event;
      if (slider.maxValue < slider.minValue) {
        slider.minValue = slider.maxValue;
      }
    }

    const range: PerformanceRangeValue = {
      min: performanceStepIndexToValue(slider.minValue),
      max: performanceStepIndexToValue(slider.maxValue),
    };
    const stored = isDefaultPerformanceRange(range) ? null : range;
    this.draft.update((d) => ({
      ...d,
      [this.performanceDraftKey(platform)]: {
        ...this.performanceDraft(d, platform),
        [metric]: stored,
      },
    }));
  }

  engagementLabel(value: number | null): string {
    return this.engagementRateOptions.find((option) => option.value === value)?.label ?? 'Any';
  }

  onFollowerRangeChange(platform: FollowerPlatformKey, event: number, type: 'min' | 'max'): void {
    const slider = this.getFollowerSlider(platform);
    if (type === 'min') {
      slider.minValue = event;
      if (slider.minValue > slider.maxValue) {
        slider.maxValue = slider.minValue;
      }
    } else {
      slider.maxValue = event;
      if (slider.maxValue < slider.minValue) {
        slider.minValue = slider.maxValue;
      }
    }

    const range: FollowerRangeValue = {
      min: followerStepIndexToValue(slider.minValue),
      max: followerStepIndexToValue(slider.maxValue),
    };
    const stored = isDefaultFollowerRange(range) ? null : range;
    this.draft.update((d) => ({ ...d, [this.draftRangeKey(platform)]: stored }));
  }

  setVerifiedOnly(checked: boolean): void {
    this.draft.update((d) => ({ ...d, isVerified: checked }));
  }

  setHasReviews(checked: boolean): void {
    this.draft.update((d) => ({ ...d, hasReviews: checked }));
  }

  private dtoFilters(): FilterCreatorsDto {
    const applied: FilterCreatorsDto = { ...(this.magicSearchService.appliedFilters ?? {}) };
    delete (applied as FilterCreatorsDto & { sort?: unknown }).sort;
    return applied;
  }

  private publishFilters(filters: FilterCreatorsDto): void {
    const next: FilterCreatorsDto = { ...filters };
    delete (next as FilterCreatorsDto & { sort?: unknown }).sort;
    if (!next.searchTerm) {
      delete next.searchTerm;
    }

    this.magicSearchService.appliedFilters = next;
    this.magicSearchService.filtersObjectSignal.update((current) => {
      const sort = (current as FilterCreatorsDto & { sort?: unknown }).sort;
      const merged = mergeProfileFilters(current, next);
      if (sort) {
        (merged as FilterCreatorsDto & { sort?: unknown }).sort = sort;
      }
      if (!next.searchTerm) {
        delete merged.searchTerm;
      }
      return merged;
    });
    this.magicSearchService.kickOffBlueSearch(next);
  }

  private syncAgeSliderFromDraft(draft: FindCreatorsFilterDraft): void {
    if (draft.ageFrom == null && draft.ageTo == null) {
      this.ageSlider.minValue = this.ageRangeSliderDefaults.min;
      this.ageSlider.maxValue = this.ageRangeSliderDefaults.max;
      return;
    }
    this.ageSlider.minValue = draft.ageFrom ?? this.ageRangeSliderDefaults.min;
    this.ageSlider.maxValue = draft.ageTo ?? this.ageRangeSliderDefaults.max;
    if (this.ageSlider.minValue > this.ageSlider.maxValue) {
      const swap = this.ageSlider.minValue;
      this.ageSlider.minValue = this.ageSlider.maxValue;
      this.ageSlider.maxValue = swap;
    }
  }

  private pushFollowerChip(
    chips: FindCreatorsFilterChip[],
    key: FindCreatorsFilterChipKey,
    label: string,
    from?: number | null,
    to?: number | null,
  ): void {
    if (from == null && to == null) {
      return;
    }
    chips.push({
      key,
      label: `${label}: ${formatFollowerRangeLabel({
        min: from ?? FOLLOWER_RANGE_DEFAULT.min,
        max: to ?? FOLLOWER_RANGE_DEFAULT.max,
      })}`,
    });
  }

  private getFollowerSlider(platform: FollowerPlatformKey): FollowerSlider {
    switch (platform) {
      case 'tiktok':
        return this.tiktokFollowerSlider;
      case 'instagram':
        return this.instagramFollowerSlider;
      case 'youtube':
        return this.youtubeFollowerSlider;
      case 'snapchat':
        return this.snapchatFollowerSlider;
    }
  }

  private draftRangeKey(
    platform: FollowerPlatformKey,
  ): 'tikTokFollowers' | 'instagramFollowers' | 'youtubeSubscribers' | 'snapchatFollowers' {
    switch (platform) {
      case 'tiktok':
        return 'tikTokFollowers';
      case 'instagram':
        return 'instagramFollowers';
      case 'youtube':
        return 'youtubeSubscribers';
      case 'snapchat':
        return 'snapchatFollowers';
    }
  }

  private syncSlidersFromDraft(): void {
    const draft = this.draft();
    this.applyRangeToSlider(this.tiktokFollowerSlider, draft.tikTokFollowers);
    this.applyRangeToSlider(this.instagramFollowerSlider, draft.instagramFollowers);
    this.applyRangeToSlider(this.youtubeFollowerSlider, draft.youtubeSubscribers);
    this.applyRangeToSlider(this.snapchatFollowerSlider, draft.snapchatFollowers);
    this.syncAgeSliderFromDraft(draft);
    this.applyPerformanceRangeToSlider(this.tiktokAvgViewsSlider, draft.tikTokPerformance.averageViews);
    this.applyPerformanceRangeToSlider(this.tiktokTotalViewsSlider, draft.tikTokPerformance.totalViews);
    this.applyPerformanceRangeToSlider(this.tiktokAvgLikesSlider, draft.tikTokPerformance.averageLikes);
    this.applyPerformanceRangeToSlider(this.tiktokTotalLikesSlider, draft.tikTokPerformance.totalLikes);
    this.applyPerformanceRangeToSlider(this.instagramAvgViewsSlider, draft.instagramPerformance.averageViews);
    this.applyPerformanceRangeToSlider(this.instagramTotalViewsSlider, draft.instagramPerformance.totalViews);
    this.applyPerformanceRangeToSlider(this.instagramAvgLikesSlider, draft.instagramPerformance.averageLikes);
    this.applyPerformanceRangeToSlider(this.instagramTotalLikesSlider, draft.instagramPerformance.totalLikes);
    this.applyPerformanceRangeToSlider(this.youtubeAvgViewsSlider, draft.youtubePerformance.averageViews);
    this.applyPerformanceRangeToSlider(this.youtubeTotalViewsSlider, draft.youtubePerformance.totalViews);
    this.applyPerformanceRangeToSlider(this.youtubeAvgLikesSlider, draft.youtubePerformance.averageLikes);
    this.applyPerformanceRangeToSlider(this.youtubeTotalLikesSlider, draft.youtubePerformance.totalLikes);
  }

  private applyPerformanceRangeToSlider(slider: FollowerSlider, range: PerformanceRangeValue | null): void {
    if (!range) {
      slider.minValue = 0;
      slider.maxValue = PERFORMANCE_RANGE_MAX_INDEX;
      return;
    }
    slider.minValue = performanceValueToStepIndex(range.min);
    slider.maxValue = performanceValueToStepIndex(range.max);
    if (slider.minValue > slider.maxValue) {
      const swap = slider.minValue;
      slider.minValue = slider.maxValue;
      slider.maxValue = swap;
    }
  }

  private performanceDraft(
    draft: FindCreatorsFilterDraft,
    platform: PerformancePlatform,
  ): PlatformPerformanceDraft {
    switch (platform) {
      case 'tiktok':
        return draft.tikTokPerformance;
      case 'instagram':
        return draft.instagramPerformance;
      case 'youtube':
        return draft.youtubePerformance;
    }
  }

  private performanceDraftKey(
    platform: PerformancePlatform,
  ): 'tikTokPerformance' | 'instagramPerformance' | 'youtubePerformance' {
    switch (platform) {
      case 'tiktok':
        return 'tikTokPerformance';
      case 'instagram':
        return 'instagramPerformance';
      case 'youtube':
        return 'youtubePerformance';
    }
  }

  private getPerformanceSlider(
    platform: PerformancePlatform,
    metric: PerformanceMetricKey,
  ): FollowerSlider {
    const sliders = {
      tiktok: {
        averageViews: this.tiktokAvgViewsSlider,
        totalViews: this.tiktokTotalViewsSlider,
        averageLikes: this.tiktokAvgLikesSlider,
        totalLikes: this.tiktokTotalLikesSlider,
      },
      instagram: {
        averageViews: this.instagramAvgViewsSlider,
        totalViews: this.instagramTotalViewsSlider,
        averageLikes: this.instagramAvgLikesSlider,
        totalLikes: this.instagramTotalLikesSlider,
      },
      youtube: {
        averageViews: this.youtubeAvgViewsSlider,
        totalViews: this.youtubeTotalViewsSlider,
        averageLikes: this.youtubeAvgLikesSlider,
        totalLikes: this.youtubeTotalLikesSlider,
      },
    };
    return sliders[platform][metric];
  }

  private pushPerformanceChips(
    chips: FindCreatorsFilterChip[],
    filters: FilterCreatorsDto,
    prefix: 'tikTok' | 'instagram' | 'youtube',
    label: string,
  ): void {
    const record = filters as Record<string, number | null | undefined>;
    const engagement = record[`${prefix}AverageEngagementFrom`];
    if (typeof engagement === 'number') {
      chips.push({
        key: `${prefix}AverageEngagement`,
        label: `${label} ER ${this.engagementLabel(engagement)}`,
      });
    }
    this.pushNamedRangeChip(
      chips,
      `${prefix}AverageViews`,
      `${label} avg views`,
      record[`${prefix}AverageViewsFrom`],
      record[`${prefix}AverageViewsTo`],
    );
    this.pushNamedRangeChip(
      chips,
      `${prefix}TotalViews`,
      `${label} views`,
      record[`${prefix}TotalViewsFrom`],
      record[`${prefix}TotalViewsTo`],
    );
    this.pushNamedRangeChip(
      chips,
      `${prefix}AverageLikes`,
      `${label} avg likes`,
      record[`${prefix}AverageLikesFrom`],
      record[`${prefix}AverageLikesTo`],
    );
    this.pushNamedRangeChip(
      chips,
      `${prefix}TotalLikes`,
      `${label} likes`,
      record[`${prefix}TotalLikesFrom`],
      record[`${prefix}TotalLikesTo`],
    );
  }

  private pushNamedRangeChip(
    chips: FindCreatorsFilterChip[],
    key: FindCreatorsFilterChipKey,
    label: string,
    from?: number | null,
    to?: number | null,
  ): void {
    if (from == null && to == null) {
      return;
    }
    chips.push({
      key,
      label: `${label}: ${formatPerformanceRangeLabel({
        min: from ?? 0,
        max: to ?? performanceStepIndexToValue(PERFORMANCE_RANGE_MAX_INDEX),
      })}`,
    });
  }

  private applyRangeToSlider(slider: FollowerSlider, range: FollowerRangeValue | null): void {
    if (!range) {
      slider.minValue = 0;
      slider.maxValue = FOLLOWER_RANGE_MAX_INDEX;
      return;
    }
    slider.minValue = followerValueToStepIndex(range.min);
    slider.maxValue = followerValueToStepIndex(range.max);
    if (slider.minValue > slider.maxValue) {
      const swap = slider.minValue;
      slider.minValue = slider.maxValue;
      slider.maxValue = swap;
    }
  }
}
